import {Component, Input, OnInit} from '@angular/core';
import {CensusRestService} from '../../../shared/services/nso-data-portal/rest-services/census-rest.service';
import {Observable} from 'rxjs';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {
  CensusAgeGroup,
  CensusBeneficiary,
  CensusData,
  CensusPeriodData,
  DonutGraph,
  ShadowCensusData
} from '../../../shared/types/nso-data-portal/census-data';
import {AddCustomAgeRangesService} from '../../../shared/services/nso-data-portal/add-services/add-custom-age-ranges-service';
import {
  OrganizationInformationRestService
} from '../../../shared/services/nso-data-portal/rest-services/organization-information-rest.service';
import {GlobalMessagesService} from '../../../shared/services/messages/global.messages.service';
import {TranslateService} from '@ngx-translate/core';
import {SecurityService} from '../../../shared/services/security/security.service';
import * as _ from 'lodash';
import {BeneficiaryRestService} from '../../../shared/services/nso-data-portal/rest-services/beneficiary-rest.service';
import {tap} from 'rxjs/operators';
import {ChartDataLabelsPlugin} from '../../../shared/chart-plugins/chart-data-labels-plugin';
import {BENEFICIARY_CHART_OPTIONS} from '../../../shared/types/nso-data-portal/charts/chart-options';
import {SearchEntityLevels} from '../../../shared/types/nso-data-portal/search-entity-levels';

@Component({
  selector: 'wsd-census-detail',
  templateUrl: './census-detail.component.html',
  styleUrls: ['./census-detail.component.css']
})
export class CensusDetailComponent implements OnInit {

  @Input()
  entityId: number;

  @Input()
  entityLevel: SearchEntityLevels;

  @Input()
  isFederation: boolean;

  @Input()
  isNSO: boolean;

  lastCensusYear: number;

  showNoDataForYearWarning: boolean;

  shadowVisible = false;

  historyReadOnly = true;

  censusData: CensusData;

  yearOptions: SelectItem[];
  selectedYear: CensusPeriodData;

  beneficiariesData: CensusBeneficiary[];

  beneficiariesGraphData: DonutGraph;

  shadowHistoricalData: ShadowCensusData;

  availableAgeGroups: string[] = [];

  beneficiaryTypes$: Observable<string[]>;

  historyData: CensusData;

  isDisableToSave: boolean;

  searchEntityLevels = SearchEntityLevels;

  readonly beneficiaryGraphOptions = BENEFICIARY_CHART_OPTIONS;


  constructor(private censusService: CensusRestService,
              private OrganizationRestService: OrganizationInformationRestService,
              private security: SecurityService,
              private message: GlobalMessagesService,
              private translate: TranslateService,
              private addService: AddCustomAgeRangesService,
              private confirmService: ConfirmationService,
              private beneficiaryService: BeneficiaryRestService,
              public chartDataLabelsPlugin: ChartDataLabelsPlugin) {
    this.security.hasPermission('NDP_ORGANIZATION_HISTORICAL_CENSUS_UPDATE').subscribe(
      hasPermission => {
        this.historyReadOnly = !hasPermission;
      }
    );
  }

  get beneficiariesTotal() {
    if (this.beneficiariesData) {
      return this.beneficiariesData
        .reduce((total: number, beneficiary: CensusBeneficiary) => {
          return total + beneficiary.total;
        }, 0) || 0;
    }

    return '';
  }

  get submitVisible() {
    return !this.isFederation && this.censusData &&
      (this.censusData.censusActive || this.censusData.censusYear || this.censusData.censusPeriod);
  }

  get readOnly() {
    return this.censusData && !this.censusData.censusActive;
  }

  get shadowCensus(): ShadowCensusData {
    return this.censusData.shadowCensusData || {} as ShadowCensusData;
  }

  get shadowCensusHistoricalData(): ShadowCensusData {
    return this.shadowHistoricalData || {} as ShadowCensusData;
  }

  get isHistoryDisabled() {
    return this.historyReadOnly || this.isFederation;
  }

  ngOnInit() {
    this.loadLastYear();
    this.loadCensus();
    this.loadPeriods();
    if (this.isFederation) {
      this.historyReadOnly = true;
    }
    this.beneficiaryTypes$ = this.beneficiaryService.getBeneficiaryTypes()
      .pipe(tap(types => types.push('OTHERS')));
  }

  openModalWindow() {
    this.addService.create();
  }

  onCustomAgeGroupSave(customAgeGroups) {
    this.OrganizationRestService.saveCustomAgeGroups(this.entityId, customAgeGroups).subscribe(
      () => {
        this.loadCensus();
        this.message.showSuccessMessage(this.translate.instant('DATA_PORTAL.CENSUS.DETAIL.SAVE_CUSTOM_GROUPS_SUCCESS'));
      },
      () => this.message.showErrorMessage(this.translate.instant('DATA_PORTAL.CENSUS.DETAIL.SAVE_CUSTOM_GROUPS_ERROR'))
    );
  }

  saveCensus() {
    this.isDisableToSave = true;
    this.OrganizationRestService.updateCensus(this.entityId, this.censusData).subscribe(
      (census) => {
        this.censusData = census;
      },
      () => {
        this.message.showErrorMessage(this.translate.instant('DATA_PORTAL.CENSUS.SAVE_CENSUS_ERROR'));
        this.isDisableToSave = false;
      },
      () => {
        this.isDisableToSave = false;
        this.message.showSuccessMessage(this.translate.instant('DATA_PORTAL.CENSUS.SAVE_CENSUS_SUCCESS'));
        this.censusData.censusActive = true;
      }
    );
  }

  checkIfYearHasData(data) {
    this.showNoDataForYearWarning = !data.ageGroups[0].id;
  }

  loadHistoryData(item) {
    if (item.value) {
      this.OrganizationRestService.getHistoryCensusData(this.entityId, item.value.year)
        .subscribe((res) => {
          this.historyData = res;
          this.checkIfYearHasData(res);
          this.initAgeGroups(res.ageGroups);
        });
    }
  }

  initAgeGroups(data: CensusAgeGroup[]) {
    this.availableAgeGroups = [];
    Object.entries(data).forEach((item) => {
      if (!this.availableAgeGroups.includes(item[1].ageGroupData.groupType)) {
        this.availableAgeGroups.push(item[1].ageGroupData.groupType);
      }
    });
  }

  saveHistoryData() {
    if (this.selectedYear.editable) {
      this.confirmService.confirm({
        header: this.translate.instant('DATA_PORTAL.CENSUS.CONFIRM_SAVE_HISTORY_CENSUS_HEADER'),
        message: this.translate.instant('DATA_PORTAL.CENSUS.CONFIRM_SAVE_HISTORY_CENSUS'),
        accept: () => {
          this.isDisableToSave = true;
          this.OrganizationRestService.updateCensus(this.entityId, this.historyData).subscribe(
            (census) => {
              this.historyData = census;
              this.loadPeriods();
              this.message.showSuccessMessage(this.translate.instant('DATA_PORTAL.CENSUS.SAVE_CENSUS_SUCCESS'));
            },
            () => this.message.showErrorMessage(this.translate.instant('DATA_PORTAL.CENSUS.SAVE_CENSUS_ERROR')),
            () => this.isDisableToSave = false
          );
        }
      });
    } else {
      this.confirmService.confirm({
        header: this.translate.instant('DATA_PORTAL.CENSUS.CONFIRM_EDIT_HISTORIC_CENSUS_HEADER'),
        message: this.translate.instant('DATA_PORTAL.CENSUS.CONFIRM_EDIT_HISTORIC_CENSUS_MESSAGE', {
          yearFrom: this.lastCensusYear - 1,
          yearTo: this.lastCensusYear
        }),
        acceptLabel: this.translate.instant('DATA_PORTAL.CENSUS.CONFIRM_SAVE_HISTORY_CENSUS_BUTTON'),
        rejectLabel: this.translate.instant('DATA_PORTAL.CENSUS.KEEP_HISTORY_CENSUS_BUTTON'),
        accept: () => {
          this.isDisableToSave = true;
          this.OrganizationRestService.updateHistoricalCensus(this.entityId, this.historyData).subscribe(
            (census) => {
              this.historyData = census;
              this.loadPeriods();
              this.message.showSuccessMessage(this.translate.instant('DATA_PORTAL.CENSUS.EDIT_HISTORIC_CENSUS_SUCCESS'));
            },
            () => this.message.showErrorMessage(this.translate.instant('DATA_PORTAL.CENSUS.SAVE_CENSUS_ERROR')),
            () => this.isDisableToSave = false
          );
        },
        reject: () => {
          this.loadPeriods();
        }
      });
    }
  }

  recalculate(entry) {
    entry.total = entry.male + entry.female + entry.other;
  }

  initShadowAndBeneficiariesData(data) {
    this.beneficiariesData = data.beneficiaries;
    this.beneficiariesGraphData = data.beneficiariesGraph;
    this.shadowHistoricalData = data.shadowData;
  }

  private loadLastYear() {
      this.censusService.getLastYear().subscribe(lastCensusYear => {
        this.lastCensusYear = lastCensusYear;
      });
    }

  private loadPeriods() {
    if (this.entityLevel === SearchEntityLevels.ORGANIZATION) {
      this.OrganizationRestService.getCensusPeriods(this.entityId).subscribe(censusPeriods => {
        this.yearOptions = (
          censusPeriods.map(entry => {
            return {
              label: [entry.year, entry.censusTotal || null].filter(Boolean).join(' - '),
              value: entry
            };
          })
        );
        this.selectDefaultYear();
      });
    }
  }

  private selectDefaultYear() {
    const selectedYear = _.get(this.selectedYear, 'year');

    const yearItemToSelect = this.yearOptions.find(yearItem => {
      return selectedYear ? _.get(yearItem, 'value.year') === selectedYear : false;
    }) || this.yearOptions[0];

    if (yearItemToSelect) {
      this.selectedYear = yearItemToSelect.value;

      this.loadHistoryData(yearItemToSelect);
    }
  }

  private loadCensus() {
    if (this.entityLevel === this.searchEntityLevels.ORGANIZATION) {
      this.OrganizationRestService.getCensus(this.entityId).subscribe(census => {
        this.censusData = census;
        this.shadowVisible = !!this.shadowCensus.id;
      });
    } else {
      this.censusData = {} as CensusData;
      this.shadowVisible = false;
    }
  }
}
